import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { UserProfileSectionProps, ThemeMode } from "../types";
import { SpinnerIcon } from "./icons";
import { ThemeSubmenu } from "./ThemeSubmenu";
import { LanguageToggle } from "./LanguageToggle";
import { trackProfileClick, trackLogoutClick } from "../sidebarAnalytics";
import { useUserCompanyName } from "../../../../hooks/useUserCompanyName";
import { menuIcon, menuRow } from "./menuStyles";

// Menú de perfil sobrio (Daniel, 30-oct, referencia ElevenLabs): grupos separados
// por una línea, íconos grises y sin colores de adorno. Antes cada fila traía su
// color (azul, morado, amarillo) y «Cerrar sesión» en rojo, que es color de
// error, no de una acción normal.

export function UserProfileSection({
  userInfo,
  onProfileClick,
  currentTheme,
  onThemeChange,
  onLogout,
  isLoggingOut,
  isDarkMode,
  isProfileActive = false,
  hideUserCard = false,
}: UserProfileSectionProps) {
  const line = isDarkMode ? "border-white/10" : "border-black/[0.07]";
  return (
    <div>
      {!hideUserCard && <UserInfoCard userInfo={userInfo} isDarkMode={isDarkMode} />}
      <div className={`${hideUserCard ? "" : `mt-1.5 pt-1.5 border-t ${line}`}`}>
        <ProfileButton onClick={onProfileClick} isDarkMode={isDarkMode} isActive={isProfileActive} />
      </div>
      <div className={`mt-1.5 pt-1.5 border-t ${line}`}>
        <LanguageToggle isDarkMode={isDarkMode} />
        {onThemeChange && (
          <ThemeSubmenu currentTheme={currentTheme ?? ("system" as ThemeMode)} onThemeChange={onThemeChange} isDarkMode={isDarkMode} />
        )}
      </div>
      <div className={`mt-1.5 pt-1.5 border-t ${line}`}>
        <LogoutButton onClick={onLogout} isLoggingOut={isLoggingOut} isDarkMode={isDarkMode} />
      </div>
    </div>
  );
}

interface UserInfoCardProps {
  userInfo: {
    first_name?: string;
    last_name?: string;
    email?: string;
    username?: string;
  };
  isDarkMode: boolean;
}

// Quién eres y en qué compañía: lo que antes iba junto al avatar del encabezado.
function UserInfoCard({ userInfo, isDarkMode }: UserInfoCardProps) {
  const { t } = useTranslation("common");
  const companyName = useUserCompanyName();
  const role = localStorage.getItem("userRole");
  const initials =
    userInfo.first_name && userInfo.last_name
      ? `${userInfo.first_name.charAt(0)}${userInfo.last_name.charAt(0)}`.toUpperCase()
      : (userInfo.email?.charAt(0) || "U").toUpperCase();

  const displayName =
    userInfo.first_name && userInfo.last_name
      ? `${userInfo.first_name} ${userInfo.last_name}`
      : userInfo.email || t("userProfile.defaultUser");

  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-xl border ${isDarkMode ? "border-white/10 bg-white/[0.03]" : "border-black/[0.07] bg-black/[0.015]"}`}>
      <div
        className={`size-9 shrink-0 rounded-full grid place-items-center text-xs font-semibold ring-1 ${
          isDarkMode ? "bg-white/10 text-gray-100 ring-white/15" : "bg-gray-200/70 text-gray-700 ring-black/10"
        }`}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-[14px] font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>{displayName}</p>
        <p className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {userInfo.email || t("userProfile.defaultEmail")}
        </p>
        {(companyName || role) && (
          <p className={`text-[11px] truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {[companyName, role].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
}

function ProfileButton({ onClick, isDarkMode, isActive = false }: { onClick?: () => void; isDarkMode: boolean; isActive?: boolean }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => {
        trackProfileClick();
        if (onClick) onClick();
        else navigate("/profile");
      }}
      className={`${menuRow(isDarkMode)} ${isActive ? (isDarkMode ? "bg-white/[0.06]" : "bg-black/[0.04]") : ""}`}
    >
      <i className={`ri-user-line ${menuIcon(isDarkMode)}`} aria-hidden />
      <span>{t("userProfile.myProfile")}</span>
    </button>
  );
}

function LogoutButton({ onClick, isLoggingOut, isDarkMode }: { onClick?: () => void; isLoggingOut: boolean; isDarkMode: boolean }) {
  const { t } = useTranslation("common");
  return (
    <button
      type="button"
      onClick={() => {
        trackLogoutClick();
        onClick?.();
      }}
      disabled={isLoggingOut}
      className={`${menuRow(isDarkMode)} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {isLoggingOut ? (
        <>
          <SpinnerIcon className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
          <span>{t("userProfile.loggingOut")}</span>
        </>
      ) : (
        <>
          <i className={`ri-logout-box-r-line ${menuIcon(isDarkMode)}`} aria-hidden />
          <span>{t("userProfile.logout")}</span>
        </>
      )}
    </button>
  );
}
